import { gql } from '@apollo/client';

import { SALE_ORDER_FIELDS } from '@/modules/doctor-pos/doctor-pos.gql';

export const PENDING_SALE_ORDERS_QUERY = gql`
  ${SALE_ORDER_FIELDS}
  query PendingSaleOrders(
    $offset: Int
    $limit: Int
    $customer_id: ID
    $pet_id: ID
    $folio_number: String
    $veterinarian_id: ID
    $include_sent_to_doctor_pending_take: Boolean
  ) {
    pendingSaleOrders(
      offset: $offset
      limit: $limit
      customer_id: $customer_id
      pet_id: $pet_id
      folio_number: $folio_number
      veterinarian_id: $veterinarian_id
      include_sent_to_doctor_pending_take: $include_sent_to_doctor_pending_take
    ) {
      items {
        ...SaleOrderFields
      }
      page {
        total
        offset
        limit
        has_more
      }
    }
  }
`;

export const CASHIER_SALE_ORDER_QUERY = gql`
  ${SALE_ORDER_FIELDS}
  query CashierSaleOrder($id: ID!) {
    cashierSaleOrder(id: $id) {
      ...SaleOrderFields
    }
  }
`;

export const CREATE_CASHIER_MEDICAL_ORDER_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation CreateCashierMedicalOrder($input: CreateCashierMedicalOrderInput!) {
    createCashierMedicalOrder(input: $input) {
      ...SaleOrderFields
    }
  }
`;

export const UPDATE_CASHIER_MEDICAL_ORDER_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation UpdateCashierMedicalOrder($input: UpdateCashierMedicalOrderInput!) {
    updateCashierMedicalOrder(input: $input) {
      ...SaleOrderFields
    }
  }
`;

export const SEND_CASHIER_MEDICAL_ORDER_TO_DOCTOR_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation SendCashierMedicalOrderToDoctor($input: SendCashierMedicalOrderToDoctorInput!) {
    sendCashierMedicalOrderToDoctor(input: $input) {
      ...SaleOrderFields
    }
  }
`;

export const START_SINGLE_STATION_ATTENTION_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation StartSingleStationAttention($input: SendCashierMedicalOrderToDoctorInput!) {
    startSingleStationAttention(input: $input) {
      ...SaleOrderFields
    }
  }
`;

export const REASSIGN_CASHIER_PENDING_CONSULTING_ROOM_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation ReassignCashierPendingConsultingRoom(
    $input: ReassignCashierPendingConsultingRoomInput!
  ) {
    reassignCashierPendingConsultingRoom(input: $input) {
      consulting_room_occupied
      advisory_code
      order {
        ...SaleOrderFields
      }
    }
  }
`;

export const DISCARD_CASHIER_OPENING_ORDER_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation DiscardCashierOpeningOrder($input: DiscardCashierOpeningOrderInputGql!) {
    discardCashierOpeningOrder(input: $input) {
      ...SaleOrderFields
    }
  }
`;

export const ADD_CASHIER_SALE_LINE_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation AddCashierSaleLine($input: AddSaleLineInput!) {
    addCashierSaleLine(input: $input) {
      ...SaleOrderFields
    }
  }
`;

export const UPDATE_CASHIER_SALE_LINE_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation UpdateCashierSaleLine($input: UpdateSaleLineInput!) {
    updateCashierSaleLine(input: $input) {
      ...SaleOrderFields
    }
  }
`;

export const REMOVE_CASHIER_SALE_LINE_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation RemoveCashierSaleLine($sale_order_item_id: ID!) {
    removeCashierSaleLine(sale_order_item_id: $sale_order_item_id) {
      ...SaleOrderFields
    }
  }
`;

export const CHARGE_SALE_ORDER_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation ChargeSaleOrder($input: ChargeSaleOrderInputGql!) {
    chargeSaleOrder(input: $input) {
      ...SaleOrderFields
    }
  }
`;

export const CHARGE_SINGLE_STATION_ORDER_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation ChargeSingleStationOrder($input: ChargeSaleOrderInputGql!) {
    chargeSingleStationOrder(input: $input) {
      ...SaleOrderFields
    }
  }
`;

export const KIT_BREAKDOWN_LINES_QUERY = gql`
  query KitBreakdownLines($kit_id: ID!) {
    kitBreakdownLines(kit_id: $kit_id)
  }
`;

export const KIT_BREAKDOWN_LINES_BY_CODE_QUERY = gql`
  query KitBreakdownLinesByCode($kit_code: String!) {
    kitBreakdownLinesByCode(kit_code: $kit_code)
  }
`;

export const CASHIER_SHIFT_SALE_HISTORY_QUERY = gql`
  ${SALE_ORDER_FIELDS}
  query CashierShiftSaleHistory($cash_shift_id: ID!, $offset: Int, $limit: Int) {
    cashierShiftSaleHistory(cash_shift_id: $cash_shift_id, offset: $offset, limit: $limit) {
      sale_order_id
      sale_folio_number
      ticket_id
      ticket_folio_number
      paid_at
      total_cents
      status
      payment_method_summary
      tutor_name
      pet_name
      customer_snapshot_json
      pet_snapshot_json
      payment_methods {
        payment_method_id
        payment_method_code
        payment_method_name
        amount_cents
        reference_number
      }
      consulting_room_name
      sale_order {
        ...SaleOrderFields
        consulting_room_name
        customer_snapshot_json
        pet_snapshot_json
      }
    }
  }
`;

export const RETURN_SALE_ORDER_TO_DOCTOR_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation ReturnSaleOrderToDoctor($input: ReturnToDoctorInputGql!) {
    returnSaleOrderToDoctor(input: $input) {
      ...SaleOrderFields
    }
  }
`;

export const PAYMENT_METHODS_QUERY = gql`
  query PaymentMethods($offset: Int, $limit: Int) {
    paymentMethods(offset: $offset, limit: $limit, status_filter: ACTIVE) {
      items {
        id
        code
        name
        requires_reference
        affects_cash_count
        sort_order
        status
      }
    }
  }
`;

export const APPLY_SALE_LINE_DISCOUNT_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation ApplySaleLineDiscount($input: ApplyDiscountInputGql!) {
    applySaleLineDiscount(input: $input) {
      ...SaleOrderFields
    }
  }
`;

export const APPLY_CASHIER_ORDER_DISCOUNT_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation ApplyCashierOrderDiscount($input: ApplyOrderDiscountInputGql!) {
    applyCashierOrderDiscount(input: $input) {
      ...SaleOrderFields
    }
  }
`;

export const CANCEL_SALE_ORDER_MUTATION = gql`
  ${SALE_ORDER_FIELDS}
  mutation CancelSaleOrder($input: CancelSaleInputGql!) {
    cancelSaleOrder(input: $input) {
      ...SaleOrderFields
    }
  }
`;

export const CANCELLATION_REASONS_QUERY = gql`
  query CancellationReasons($offset: Int, $limit: Int) {
    cancellationReasons(offset: $offset, limit: $limit, status_filter: ACTIVE) {
      items {
        id
        code
        description
        scope
      }
    }
  }
`;

export const CASHIER_CONSULTING_ROOMS_QUERY = gql`
  query CashierConsultingRooms {
    consultingRooms(limit: 200, status_filter: ACTIVE) {
      items {
        id
        name
        display_order
        zone_type
        status
        branch_id
        workstation_id
      }
    }
  }
`;
